import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase, db, dbUpdate } from '../utils/supabase';
import { checkUsage, DEFAULT_FEATURE_MATRIX } from '../utils/monetization';
import { subscribeAuthState, loadUserProfile, updateUserProfile, logoutUser, deleteAccount } from '../utils/supabaseAuth';
import { subscribeToConversations, subscribeToMessages, sendMessage as dbSendMessage, getOrCreateConversation, loadConversations, markConversationRead } from '../utils/supabaseMessages';
import { requestLocation, attachDistances, saveLocation, loadCachedLocation } from '../utils/geolocation';
import { requestPushPermission, onForegroundMessage, showToast } from '../utils/firebaseNotifications';
import { callAPI } from '../utils/api';
import { computeRadarCompat } from '../utils/matching';
import { getInitialLang } from '../utils/i18n';

const AppContext = createContext({});
export const useApp = () => useContext(AppContext);

// ── UI-only preferences (localStorage is fine for these) ─────────────────
const loadPref  = (key, def) => { try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : def; } catch { return def; } };
const savePref  = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };
const loadStr   = (key, def) => { try { return localStorage.getItem(key) || def; } catch { return def; } };
const saveStr   = (key, val) => { try { localStorage.setItem(key, val); } catch {} };

export function AppProvider({ children }) {

  // ── Auth & current user ──────────────────────────────────────────────────
  const [authUser,     setAuthUser]     = useState(null);   // Supabase auth user
  const [currentUser,  setCurrentUser]  = useState(null);   // full profile from DB
  const [loading,      setLoading]      = useState(true);

  // ── Navigation ───────────────────────────────────────────────────────────
  const [page,         setPageState]    = useState('discover');
  const [pageStack,    setPageStack]    = useState([]);
  const [activeChat,   setActiveChatState] = useState(null);

  // ── Discover state ───────────────────────────────────────────────────────
  const [candidates,   setCandidates]   = useState([]);     // from discover_candidates()
  const [likes,        setLikes]        = useState(new Set());
  const [passes,       setPasses]       = useState(new Set());
  const [superLikes,   setSuperLikes]   = useState(new Set());
  const [matches,      setMatches]      = useState([]);

  // ── Conversations (Realtime) ─────────────────────────────────────────────
  const [conversations, setConversations] = useState([]);
  const [activeMessages, setActiveMessages] = useState([]);
  const convUnsubRef    = useRef(null);
  const msgUnsubRef     = useRef(null);
  const notifChannelRef = useRef(null);

  // ── Events ───────────────────────────────────────────────────────────────
  const [events,            setEvents]           = useState([]);
  const [registeredEvents,  setRegisteredEvents] = useState(new Set());
  const [attendedEvents,    setAttendedEvents]   = useState(new Set());
  const [waitlistEvents,    setWaitlistEvents]    = useState(new Set());

  // ── Scouts ──────────────────────────────────────────────────────────────
  const [scouts,            setScouts]           = useState([]);
  const [waitlistEntries,   setWaitlistEntries]  = useState([]);

  // ── Notifications ────────────────────────────────────────────────────────
  const [notifications,  setNotifications] = useState([]);
  const [unread,         setUnread]        = useState(0);

  // ── UI preferences (localStorage only) ───────────────────────────────────
  const [theme,         setThemeState]  = useState(() => loadStr('beeloveTheme', 'dark'));
  const [lang,          setLangState]   = useState(() => getInitialLang());
  const [appMode,       setAppModeState]= useState(() => loadStr('beeloveAppMode', 'romantic'));
  // App config: loaded from app_config DB table (key/value JSONB)
  const [appConfig, setAppConfig] = useState({ invite_trial_days: 30 });

  // Features: loaded from app_features DB table, with localStorage fallback
  // Format: { discover: true, events: false, perk_super_likes: false, ... }
  // Core flags that are always on — they cannot be disabled by the DB or cache.
  const CORE_FLAGS_ON = { friend_rating: true };

  const [features, setFeaturesState] = useState(() => ({
    // Always-on defaults until DB loads
    discover: true, matches: true, messages: true, profile: true, notifications: true,
    discover_explicit_btns: true, discover_quota_visible: true,
    discover_rewind: true,
    gamification_milestones: true, gamification_streaks: true,
    gamification_journey: true, gamification_hive_score: true,
    ...loadPref('beeloveFeatures', {}),
    // Core flags come last so localStorage can never disable them
    ...CORE_FLAGS_ON,
  }));
  const [featureMatrix,  setFeatureMatrix]  = useState(null);
  const [dailyUsage,    setDailyUsage]    = useState({ likes:0, superLikes:0, boosts:0 }); // loaded from plan_features table
  const [profileTabs,   setProfileTabsState] = useState(() => loadPref('beeloveProfileTabs', null));

  // ── Quiz progress (localStorage for resume-ability, synced to DB on complete) ──
  const [quizDone,         setQuizDone]         = useState(false);
  const [showQuizPrompt,   setShowQuizPrompt]   = useState(false);
  const [quizPromptMatchId,setQuizPromptMatchId]= useState(null);
  const [partialQuizAnswers,setPartialQuizAnswers] = useState({});
  const [quizResumeIdx,    setQuizResumeIdx]    = useState(0);

  // ── Discovery prefs ───────────────────────────────────────────────────────
  const [discoveryPrefs, setDiscoveryPrefs] = useState({ minAge:18, maxAge:50, radiusKm:25 });

  // (people filter is owned per-page; each of Discover, BeeHive, BeeConnect
  // keeps its own filter state so a selection on one never empties the others.)

  // ── Location ─────────────────────────────────────────────────────────────
  const [location, setLocation] = useState(() => loadCachedLocation());

  // ── Boost ────────────────────────────────────────────────────────────────
  const [boost, setBoost]       = useState(false);
  const [boostEndsAt, setBoostEndsAt] = useState(null);
  const [lastPassedId, setLastPassedId] = useState(null); // for rewind

  // ── Networks ─────────────────────────────────────────────────────────────
  const [networks,         setNetworks]         = useState([]);
  const [activeNetwork,    setActiveNetwork]     = useState(() => {
    return 'ca_montreal';
  });
  const [networkFeatures,  setNetworkFeatures]   = useState({});  // { events: true, circles: false, ... }

  // =========================================================================
  // AUTH — subscribe to Supabase auth state
  // =========================================================================
  useEffect(() => {
    // Load app feature flags (not user-dependent — load immediately)
    supabase?.from('app_features').select('key, enabled').then(({ data }) => {
      if (!data?.length) return;
      const flags = {};
      data.forEach(({ key, enabled }) => { flags[key] = enabled; });
      setFeaturesState(prev => {
        const merged = { ...prev, ...flags, ...CORE_FLAGS_ON };
        savePref('beeloveFeatures', merged);
        return merged;
      });
    });

    supabase?.from('app_config').select('key, value').then(({ data }) => {
      if (!data?.length) return;
      const cfg = {};
      data.forEach(({ key, value }) => { cfg[key] = value; });
      setAppConfig(prev => ({ ...prev, ...cfg }));
    });

    // Load network feature flags (per-city)
    if (activeNetwork) {
      supabase?.from('network_features').select('feature, enabled').eq('network_id', activeNetwork).then(({ data }) => {
        if (!data?.length) return;
        const flags = {};
        data.forEach(({ feature, enabled }) => { flags[feature] = enabled; });
        setNetworkFeatures(flags);
      });
    }

    // Load plan feature matrix
    supabase?.from('plan_features').select('plan, feature_key, enabled, limit_value').then(({ data }) => {
      if (!data?.length) return;
      const matrix = { free:{}, honey:{}, royal:{}, founder:{} };
      data.forEach(({ plan, feature_key, enabled, limit_value }) => {
        if (matrix[plan]) matrix[plan][feature_key] = { enabled, limit: limit_value };
      });
      // founder gets all royal features (DB has no founder rows — mirror royal)
      matrix.founder = { ...matrix.royal };
      matrix.founder.beta_features = { enabled: true, limit: null };
      setFeatureMatrix(matrix);
    });

        const unsub = subscribeAuthState(async (user) => {
      setAuthUser(user);
      if (user) {
        let { profile } = await loadUserProfile(user.id);
        if (!profile) {
          // Trigger may not have fired yet (new user or local dev) — bootstrap minimal rows
          try {
            await supabase?.from('user_core').upsert({
              id:    user.id,
              email: user.email || '',
              name:  user.user_metadata?.name || user.email?.split('@')[0] || '',
            }, { onConflict: 'id' });
            await supabase?.from('user_profiles').upsert({ user_id: user.id }, { onConflict: 'user_id' });
            await supabase?.from('user_scores').upsert({ user_id: user.id }, { onConflict: 'user_id' });
          } catch {}
          ({ profile } = await loadUserProfile(user.id));
        }
        if (profile) {
          setCurrentUser(profile);
          setQuizDone(Object.keys(profile.quiz_answers || {}).length >= 8);
          setAppModeState(profile.app_mode || 'romantic');
          if (profile.network_id) setActiveNetwork(profile.network_id);
          await loadUserData(user.id, profile);
        }
      } else {
        setCurrentUser(null);
        resetUserState();
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Last-seen heartbeat ──────────────────────────────────────────────────
  // Throttled to 5min — calls touch_last_seen RPC on mount + window focus.
  // Powers Admin Growth "Inactive 7d+" cohort and Users tab "Last seen" stat.
  const lastTouchedRef = useRef(0);
  useEffect(() => {
    if (!authUser?.id || !supabase) return;
    const THROTTLE_MS = 5 * 60 * 1000;
    const touch = () => {
      if (Date.now() - lastTouchedRef.current < THROTTLE_MS) return;
      lastTouchedRef.current = Date.now();
      supabase.rpc('touch_last_seen').then(({ error }) => {
        if (error) lastTouchedRef.current = 0; // allow retry
      });
    };
    touch();
    const onFocus = () => touch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authUser?.id]);

  // Reload per-city features whenever active network changes
  useEffect(() => {
    if (!supabase || !activeNetwork) return;
    supabase.from('network_features').select('feature, enabled').eq('network_id', activeNetwork).then(({ data }) => {
      if (!data?.length) return;
      const flags = {};
      data.forEach(({ feature, enabled }) => { flags[feature] = enabled; });
      setNetworkFeatures(flags);
    });
  }, [activeNetwork]);

  async function loadUserData(uid, profile) {
    if (!supabase) return;

    // Load likes, passes, matches in parallel
    const [likesRes, passesRes, matchesRes, regRes, notifRes] = await Promise.all([
      db.likes()?.select('to_user').eq('from_user', uid),
      db.passes()?.select('to_user').eq('from_user', uid),
      db.matches()?.select('*, user_a_profile:user_core!matches_user_a_fkey(id,name,age,user_profiles(photos,job,city,bio,interests),user_scores(criteria_scores,criteria_scores_fit)), user_b_profile:user_core!matches_user_b_fkey(id,name,age,user_profiles(photos,job,city,bio,interests),user_scores(criteria_scores,criteria_scores_fit))').or(`user_a.eq.${uid},user_b.eq.${uid}`),
      db.event_registrations()?.select('event_id,confirmed,attended').eq('user_id', uid),
      db.notifications()?.select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(30),
    ]);

    if (likesRes?.data)  setLikes(new Set(likesRes.data.map(r => r.to_user)));
    if (passesRes?.data) setPasses(new Set(passesRes.data.map(r => r.to_user)));
    if (matchesRes?.data) {
      setMatches(matchesRes.data
        .filter(m => m.status !== 'declined')
        .map(m => {
          const raw = m.user_a === uid ? m.user_b_profile : m.user_a_profile;
          const { user_profiles, user_scores, ...coreFields } = raw || {};
          // user_profiles and user_scores use user_id as PK → Supabase returns them
          // as objects (1:1), not arrays. Support both shapes defensively.
          const profileFields = (Array.isArray(user_profiles) ? user_profiles[0] : user_profiles) || {};
          const scoreFields   = (Array.isArray(user_scores)   ? user_scores[0]   : user_scores)   || {};
          return { ...m, ...coreFields, ...profileFields, ...scoreFields, matchId: m.id, compatScore: m.compat_score, matchedAt: m.matched_at };
        }));
    }
    if (regRes?.data) {
      setRegisteredEvents(new Set(regRes.data.filter(r => r.confirmed).map(r => r.event_id)));
      setAttendedEvents(new Set(regRes.data.filter(r => r.attended).map(r => r.event_id)));
    }
    if (notifRes?.data) {
      setNotifications(notifRes.data);
      setUnread(notifRes.data.filter(n => !n.read).length);
    }

    // Discover candidates via PostGIS function
    loadCandidates(uid, profile);

    // Load events
    loadEvents(profile.network_id || 'ca_montreal');

    // Load conversations immediately, then keep them live via realtime.
    loadConversations(uid).then(convs => { if (convs?.length) setConversations(convs); });
    if (convUnsubRef.current) convUnsubRef.current();
    convUnsubRef.current = subscribeToConversations(uid, (convs) => {
      setConversations(convs);
    });

    // Notifications Realtime — removeChannel purges Supabase's internal registry,
    // preventing "cannot add callbacks after subscribe()" on re-entry.
    if (notifChannelRef.current) {
      supabase.removeChannel(notifChannelRef.current);
      notifChannelRef.current = null;
    }
    notifChannelRef.current = supabase
      ?.channel(`notif:${uid}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}` },
        (payload) => {
          setNotifications(prev => [payload.new, ...prev]);
          setUnread(n => n + 1);
          const notifData = typeof payload.new.data === 'string' ? JSON.parse(payload.new.data) : (payload.new.data || {});
          const notifMatchUserId = (payload.new.type === 'new_match' || payload.new.type === 'match') ? notifData.matchUserId : null;
          showToast({
            title: payload.new.title,
            body: payload.new.body,
            ...(notifMatchUserId ? { onClick: () => {
              sessionStorage.setItem('openMatchUserId', String(notifMatchUserId));
              import('../utils/navigate').then(({ navigate }) => navigate('matches'));
            }} : {}),
          });
        })
      .subscribe();

    return () => {
      if (notifChannelRef.current) {
        supabase.removeChannel(notifChannelRef.current);
        notifChannelRef.current = null;
      }
    };
  }

  async function loadCandidates(uid, profile) {
    if (!supabase) return;
    const { data } = await supabase.rpc('discover_candidates', {
      viewer_id: uid,
      radius_km: discoveryPrefs.radiusKm,
      lim: 80,
    });
    if (data) setCandidates(data);
  }

  async function loadEvents(networkId) {
    if (!supabase) return;
    const { data } = await db.events_full()
      ?.select('*')
      .in('status', ['published', 'past'])
      .in('network_id', [networkId, 'all'])
      .order('date', { ascending: true });
    if (data) setEvents(data);
  }

  function resetUserState() {
    setMatches([]); setLikes(new Set()); setPasses(new Set());
    setConversations([]); setNotifications([]); setUnread(0);
    setRegisteredEvents(new Set()); setAttendedEvents(new Set());
    setCandidates([]);
    if (convUnsubRef.current) convUnsubRef.current();
    if (msgUnsubRef.current) msgUnsubRef.current();
  }

  // =========================================================================
  // DISCOVER — like, pass, super like
  // =========================================================================
  const likeUser = useCallback(async (userId) => {
    if (!authUser || !currentUser) return;

    // Check daily like limit — bypass when subscriptions are disabled
    if (features?.subscriptions_enabled === true) {
      const usage = await checkUsage(currentUser.id, currentUser.plan, 'likes_per_day', true);
      if (!usage.allowed) {
        showToast({ title: lang === 'fr' ? `Limite quotidienne atteinte (${usage.limit} likes)` : `Daily likes limit reached (${usage.limit})`, body: lang === 'fr' ? 'Passe à Honey pour des likes illimités.' : 'Upgrade to Honey for unlimited likes.' });
        return;
      }
      setDailyUsage(d => ({ ...d, likes: (d.likes || 0) + 1 }));
    }

    setLikes(p => new Set([...p, userId]));

    if (!supabase) return;
    const { error } = await db.likes()?.insert({
      from_user: currentUser.id,
      to_user:   userId,
      is_super:  false,
    });
    if (error) { setLikes(p => { const n = new Set(p); n.delete(userId); return n; }); return; }

    // Check for mutual match
    const { data: mutual } = await db.likes()?.select('id')
      .eq('from_user', userId).eq('to_user', currentUser.id).single();
    if (mutual) {
      // Match created by DB trigger — load it
      const { data: match } = await db.matches()?.select('*')
        .or(`user_a.eq.${currentUser.id},user_b.eq.${currentUser.id}`)
        .eq(currentUser.id < userId ? 'user_b' : 'user_a', userId)
        .single();
      if (match) {
        const other = candidates.find(u => u.id === userId) || { id: userId };
        const newMatch = { ...match, ...other, matchId: match.id };
        setMatches(prev => {
          if (prev.find(m => m.matchId === match.id)) return prev;
          return [...prev, newMatch];
        });
        if (!quizDone && match.status === 'accepted') {
          setShowQuizPrompt(true); setQuizPromptMatchId(userId);
        }
      }
    }
  }, [authUser, currentUser, candidates, quizDone]);

  const passUser = useCallback(async (userId) => {
    if (!authUser || !currentUser) return;
    setPasses(p => new Set([...p, userId]));
    setLastPassedId(userId);
    // Track timestamp for "second regard" (requeue after 7 days)
    try {
      const ts = JSON.parse(localStorage.getItem('beelovePassTs') || '{}');
      ts[userId] = Date.now();
      localStorage.setItem('beelovePassTs', JSON.stringify(ts));
    } catch {}

    if (!supabase) return;
    const { error: passErr } = await db.passes()?.insert({ from_user: currentUser.id, to_user: userId }) || {};
    if (passErr) { setPasses(p => { const n = new Set(p); n.delete(userId); return n; }); return; }
    // Track for rewind
    await supabase.rpc('record_pass_for_rewind', {
      p_passer_id: currentUser.id, p_passed_id: userId,
    }).then(undefined, () => {});
  }, [authUser, currentUser, supabase]);

  const superLikeUser = useCallback(async (userId) => {
    if (!authUser || !currentUser) return;

    // Check super like limit — bypass when subscriptions are disabled
    if (features?.subscriptions_enabled === true) {
      const usage = await checkUsage(currentUser.id, currentUser.plan, 'super_likes_per_day', true);
      if (!usage.allowed) {
        showToast({ title: lang === 'fr' ? `Limite super likes atteinte (${usage.limit})` : `Super likes limit reached (${usage.limit})`, body: lang === 'fr' ? 'Passe à Honey pour plus de super likes.' : 'Upgrade to Honey for more super likes.' });
        return;
      }
      setDailyUsage(d => ({ ...d, superLikes: (d.superLikes || 0) + 1 }));
    }

    setSuperLikes(p => new Set([...p, userId]));
    setLikes(p => new Set([...p, userId]));
    if (!supabase) return;
    await db.likes()?.insert({ from_user: currentUser.id, to_user: userId, is_super: true })
      .then(undefined, () => {});
  }, [authUser, currentUser]);

  const acceptConnection = useCallback(async (matchId) => {
    if (!supabase || !currentUser) return;
    const { error } = await supabase.rpc('accept_connection', {
      p_match_id: matchId,
      p_user_id: currentUser.id,
    });
    if (!error) {
      setMatches(prev => prev.map(m => m.matchId === matchId ? { ...m, status: 'accepted' } : m));
    }
  }, [supabase, currentUser]);

  const declineConnection = useCallback(async (matchId) => {
    if (!supabase || !currentUser) return;
    const { error } = await supabase.rpc('decline_connection', {
      p_match_id: matchId,
      p_user_id: currentUser.id,
    });
    if (!error) {
      setMatches(prev => prev.filter(m => m.matchId !== matchId));
    }
  }, [supabase, currentUser]);

  // =========================================================================
  // MESSAGES — real Supabase Realtime
  // =========================================================================
  const sendMessage = useCallback(async (userId, text, opts = {}) => {
    if (!currentUser || !text?.trim()) return;
    if (!supabase) return;
    const convId = await getOrCreateConversation(currentUser.id, userId);
    // Optimistic insert — show message immediately without waiting for Realtime
    const whisper = opts.whisper === true && features?.feature_whisper === true;
    const optimistic = {
      id: `opt-${Date.now()}`,
      conversation_id: convId,
      sender_id: currentUser.id,
      text: text.trim(),
      created_at: new Date().toISOString(),
      seen_at: null,
      is_whisper: whisper,
      expires_at: whisper ? new Date(Date.now() + 24 * 3600 * 1000).toISOString() : null,
    };
    setActiveMessages(prev => [...(prev || []), optimistic]);
    const { error } = await dbSendMessage(convId, currentUser.id, text, { whisper });
    if (error) {
      // Roll back optimistic message on failure
      setActiveMessages(prev => (prev || []).filter(m => m.id !== optimistic.id));
    }
    // Realtime will replace the optimistic row with the real one when it fires
  }, [currentUser, features?.feature_whisper]);

  // Wrap setActiveChat: refresh conversation list when closing a chat so it
  // appears immediately even if the realtime event hasn't fired yet.
  const setActiveChat = useCallback((id) => {
    setActiveChatState(id);
    if (!id && currentUser?.id) {
      loadConversations(currentUser.id).then(convs => setConversations(convs || []));
    }
  }, [currentUser?.id]);

  // Subscribe to messages when activeChat changes
  useEffect(() => {
    if (!activeChat || !currentUser || !supabase) return;
    if (msgUnsubRef.current) msgUnsubRef.current();
    getOrCreateConversation(currentUser.id, activeChat).then(convId => {
      msgUnsubRef.current = subscribeToMessages(convId, setActiveMessages);
    });
    return () => { if (msgUnsubRef.current) msgUnsubRef.current(); };
  }, [activeChat, currentUser]);

  // =========================================================================
  // EVENTS — register / unregister
  // =========================================================================
  const registerForEvent = useCallback(async (eventId, paymentId = null, amountPaid = 0) => {
    if (!currentUser) return { error: 'Not logged in' };
    setRegisteredEvents(prev => new Set([...prev, eventId]));
    if (!supabase) return { error: null };
    const { error } = await db.event_registrations()?.upsert({
      event_id:       eventId,
      user_id:        currentUser.id,
      confirmed:      true,
      payment_status: paymentId ? 'succeeded' : 'pending',
      payment_id:     paymentId,
      amount_paid:    amountPaid,
    }, { onConflict: 'event_id,user_id' });
    if (error) {
      setRegisteredEvents(prev => { const n = new Set(prev); n.delete(eventId); return n; });
      return { error: error.message };
    }
    return { error: null };
  }, [currentUser]);

  // ── Cancel event registration ────────────────────────────────────────────
  const cancelEventRegistration = useCallback(async (eventId, reason = '') => {
    if (!currentUser) return { error: 'Not logged in' };
    setRegisteredEvents(prev => { const n = new Set(prev); n.delete(eventId); return n; });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await callAPI('/events/cancel-registration', 'POST', { eventId, reason }, session?.access_token);
      if (res?.error) {
        setRegisteredEvents(prev => new Set([...prev, eventId]));
        return { error: res.error };
      }
      return { error: null, refundProcessed: res?.refundProcessed };
    } catch (e) {
      setRegisteredEvents(prev => new Set([...prev, eventId]));
      return { error: e.message };
    }
  }, [currentUser, supabase]);

  const joinEventWaitlist = useCallback(async (eventId) => {
    if (!currentUser) return { error: 'Not logged in' };
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await callAPI('/events/waitlist', 'POST', { eventId, action: 'join' }, session?.access_token);
      if (!res?.error) {
        setWaitlistEvents(prev => new Set([...prev, eventId]));
        return { position: res?.position };
      }
      return { error: res.error };
    } catch (e) { return { error: e.message }; }
  }, [currentUser, supabase]);

  const leaveEventWaitlist = useCallback(async (eventId) => {
    if (!currentUser) return;
    setWaitlistEvents(prev => { const n = new Set(prev); n.delete(eventId); return n; });
    const { data: { session } } = await supabase.auth.getSession();
    await callAPI('/events/waitlist', 'POST', { eventId, action: 'leave' }, session?.access_token);
  }, [currentUser, supabase]);

  // =========================================================================
  // CIRCLES — join waitlist, confirm/decline
  // =========================================================================
  const joinScoutWaitlist = useCallback(async ({
    networkId, scoutType,
    days_available = [], time_slots = [],
    lang_pref = 'both', group_size_pref = 'any',
    availability = 'both',
  }) => {
    if (!currentUser) return { error: 'Not logged in' };
    if (!supabase) return { error: null };
    const { error } = await db.scout_waitlist()?.upsert({
      user_id:         currentUser.id,
      network_id:      networkId || currentUser.network_id || 'ca_montreal',
      scout_type:      scoutType,
      days_available,
      time_slots,
      lang_pref,
      group_size_pref,
      availability,
    }, { onConflict: 'user_id,network_id,scout_type' });
    if (!error) {
      setWaitlistEntries(prev => [...prev, { user_id: currentUser.id, network_id: networkId, scout_type: scoutType }]);
    }
    return { error: error?.message || null };
  }, [currentUser]);

  const confirmScout = useCallback(async (scoutId, confirmed) => {
    if (!currentUser || !supabase) return;
    await db.scout_members()?.update({ confirmed }).eq('scout_id', scoutId).eq('user_id', currentUser.id);
    setScouts(prev => prev.map(c => c.id !== scoutId ? c : {
      ...c,
      members: c.members?.map(m => m.user_id === currentUser.id ? { ...m, confirmed } : m)
    }));
  }, [currentUser]);

  const voteScout = useCallback(async (scoutId, vote) => {
    if (!currentUser || !supabase) return;
    await db.scout_members()?.update({ voted: true, vote }).eq('scout_id', scoutId).eq('user_id', currentUser.id);
  }, [currentUser]);

  // ── Create a private (host-initiated) scout + send invitations ──────────
  const createPrivateScout = useCallback(async ({ activityType, proposedDates, venueType, locationArea, inviteeIds }) => {
    if (!currentUser?.id || !supabase) return { error: 'Not logged in' };
    const { data: scout, error: scoutErr } = await supabase
      .from('scouts')
      .insert({
        host_id:        currentUser.id,
        network_id:     currentUser.network_id || 'ca_montreal',
        type:           activityType,
        activity_type:  activityType,
        proposed_dates: proposedDates,
        venue_type:     venueType,
        location_area:  locationArea || null,
        is_private:     true,
        status:         'waiting',
      })
      .select('id')
      .single();
    if (scoutErr) return { error: scoutErr.message };

    const invitations = inviteeIds.map(invitee_id => ({
      scout_id:  scout.id,
      invitee_id,
      invited_by: currentUser.id,
    }));
    const { error: invErr } = await supabase.from('scout_invitations').insert(invitations);
    if (invErr) return { error: invErr.message };

    return { scout };
  }, [currentUser, supabase]);

  // ── Cancel scout membership (user withdraws) ────────────────────────────
  const cancelScout = useCallback(async (scoutId, reason = '') => {
    if (!currentUser) return;
    await db.scout_members()
      ?.update({ cancelled_at: new Date().toISOString(), cancel_reason: reason })
      .eq('scout_id', scoutId).eq('user_id', currentUser.id);
    await db.notifications()?.insert({
      user_id: currentUser.id, type: 'scout_cancelled',
      title: 'You left a Scout', body: 'Your spot has been released.',
      data: { scoutId },
    });
  }, [currentUser]);

  // ── Real boost — writes to user_boosts table ─────────────────────────────
  const triggerBoost = useCallback(async () => {
    if (!authUser || !currentUser) return;
    // Check monthly limit
    const { checkUsage } = await import('../utils/monetization');
    const usage = await checkUsage(currentUser.id, currentUser.plan, 'boosts_per_month', true);
    if (!usage.allowed) {
      showToast({ title: lang === 'fr' ? `Limite boosts atteinte (${usage.limit}/mois)` : `Boosts limit reached (${usage.limit}/month)`, body: lang === 'fr' ? 'Passe à Honey pour plus de boosts.' : 'Upgrade to Honey for more boosts.' });
      return;
    }
    const endsAt = Date.now() + 30 * 60 * 1000;
    setBoost(true);
    setBoostEndsAt(endsAt);
    // Insert into user_boosts
    await supabase?.from('user_boosts').insert({
      user_id:    currentUser.id,
      expires_at: new Date(endsAt).toISOString(),
      multiplier: 3.0,
    });
    // Auto-reset after 30 min
    setTimeout(() => { setBoost(false); setBoostEndsAt(null); }, 30 * 60 * 1000);
  }, [authUser, currentUser, supabase]);

  // ── Rewind — undo last pass ───────────────────────────────────────────────
  const rewindLastPass = useCallback(async () => {
    if (!authUser || !currentUser) return { error: 'Not logged in' };
    // Check feature access
    const { canAccess } = await import('../utils/monetization');
    if (!canAccess(currentUser, 'rewind_last_pass', featureMatrix)) {
      return { error: 'upgrade_required' };
    }
    if (!supabase) return { error: 'Supabase not configured' };
    const { data } = await supabase.rpc('rewind_last_pass', { p_user_id: currentUser.id });
    if (data?.error) return { error: data.error };
    if (data?.profileId) {
      // Remove from passes set
      setPasses(prev => { const n = new Set(prev); n.delete(data.profileId); return n; });
      setLastPassedId(null);
    }
    return { error: null, profileId: data?.profileId };
  }, [authUser, currentUser, supabase, featureMatrix]);

  // ── Toggle incognito ──────────────────────────────────────────────────────
  const toggleIncognito = useCallback(async () => {
    if (!currentUser) return;
    const newVal = !currentUser.incognito;
    setCurrentUser(u => ({ ...u, incognito: newVal }));
    await dbUpdate.userCore(currentUser.id, { incognito: newVal });
  }, [currentUser]);

  const togglePause = useCallback(async () => {
    if (!currentUser) return;
    const newVal = !currentUser.paused;
    const extra = newVal ? {} : { came_back_at: new Date().toISOString() };
    setCurrentUser(u => ({ ...u, paused: newVal, ...extra }));
    await dbUpdate.userCore(currentUser.id, { paused: newVal, ...extra });
  }, [currentUser]);

  const activateHiveBoost = useCallback(async (groupId) => {
    if (!currentUser) return;
    const boostUntil = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
    setCurrentUser(u => ({ ...u, hive_boost_group: groupId, hive_boost_until: boostUntil }));
    await dbUpdate.userCore(currentUser.id, { hive_boost_group: groupId, hive_boost_until: boostUntil });
  }, [currentUser]);

  const cancelHiveBoost = useCallback(async () => {
    if (!currentUser) return;
    setCurrentUser(u => ({ ...u, hive_boost_group: null, hive_boost_until: null }));
    await dbUpdate.userCore(currentUser.id, { hive_boost_group: null, hive_boost_until: null });
  }, [currentUser]);

  const toggleAvailable = useCallback(async () => {
    if (!currentUser) return;
    // available_until = 24h from now, or null to turn off
    const isNowAvailable = currentUser.available_until && new Date(currentUser.available_until) > new Date();
    const newVal = isNowAvailable ? null : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    setCurrentUser(u => ({ ...u, available_until: newVal }));
    await dbUpdate.userCore(currentUser.id, { available_until: newVal });
  }, [currentUser]);



  // =========================================================================
  // QUIZ — save partial + final
  // =========================================================================
  const savePartialQuiz = useCallback((answers, idx) => {
    setPartialQuizAnswers(answers);
    setQuizResumeIdx(idx);
    saveStr('beelovePartialQuiz', JSON.stringify(answers));
    saveStr('beeloveQuizIdx', String(idx));
  }, []);

  const saveQuizAnswers = useCallback(async (answers) => {
    setQuizDone(true);
    setPartialQuizAnswers({});
    setQuizResumeIdx(0);
    try { localStorage.removeItem('beelovePartialQuiz'); localStorage.removeItem('beeloveQuizIdx'); } catch {}

    // Compute criteria scores from answers
    const axes = ['narrative','stability','boredom','social','stakes','repair','conflict','accountability','generosity','presence'];
    const criteria_scores = {};
    axes.forEach(axis => {
      const val = answers[axis];
      if (val != null) criteria_scores[axis] = parseFloat((val * 2.5).toFixed(2));
    });

    setCurrentUser(u => ({ ...u, quiz_answers: answers, criteria_scores }));
    if (currentUser?.id) {
      await dbUpdate.userScores(currentUser.id, { quiz_answers: answers, criteria_scores });
    }
  }, [currentUser]);

  // =========================================================================
  // PROFILE — update photos
  // =========================================================================
  const updatePhotos = useCallback(async (photos) => {
    setCurrentUser(u => ({ ...u, photos }));
    if (currentUser?.id) {
      await dbUpdate.userProfile(currentUser.id, { photos });
    }
  }, [currentUser]);

  // =========================================================================
  // AUTH ACTIONS
  // =========================================================================
  const logout = async () => {
    await logoutUser();
    resetUserState();
    setCurrentUser(null);
    setAuthUser(null);
  };

  const deleteUserAccount = async () => {
    if (authUser?.id) await deleteAccount(authUser.id);
    resetUserState();
    setCurrentUser(null);
    setAuthUser(null);
  };

  // =========================================================================
  // SUBSCRIPTION — update after Stripe payment
  // =========================================================================
  const updateSubscription = useCallback(async (plan, expiresAt) => {
    setCurrentUser(u => ({ ...u, plan, premium: plan !== 'free', plan_expires_at: expiresAt }));
    if (currentUser?.id) {
      await dbUpdate.userCore(currentUser.id, { plan, premium: plan !== 'free' });
      await dbUpdate.userScores(currentUser.id, { plan_expires_at: expiresAt });
    }
  }, [currentUser]);

  // =========================================================================
  // NOTIFICATIONS
  // =========================================================================
  const markRead = useCallback(async (notifId) => {
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    setUnread(n => Math.max(0, n - 1));
    if (supabase && notifId) {
      await db.notifications()?.update({ read: true }).eq('id', notifId);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    if (supabase && currentUser?.id) {
      await db.notifications()?.update({ read: true }).eq('user_id', currentUser.id).eq('read', false);
    }
  }, [currentUser]);

  // =========================================================================
  // UI PREFERENCES — localStorage only
  // =========================================================================
  const setTheme     = (t) => { setThemeState(t); saveStr('beeloveTheme', t); };
  const toggleTheme  = ()  => { const t = theme === 'dark' ? 'light' : 'dark'; setThemeState(t); saveStr('beeloveTheme', t); };
  const setLang  = (l) => { setLangState(l);  saveStr('beeloveLang', l);  };
  const switchAppMode = (mode) => {
    setAppModeState(mode);
    saveStr('beeloveAppMode', mode);
    if (currentUser?.id) dbUpdate.userCore(currentUser.id, { app_mode: mode }).then(undefined, () => {});
  };
  const setFeatures = (fn) => {
    setFeaturesState(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      savePref('beeloveFeatures', next);
      return next;
    });
  };
  const setProfileTabs = (tabs) => {
    setProfileTabsState(tabs);
    savePref('beeloveProfileTabs', tabs);
  };

  // =========================================================================
  // NAVIGATION
  // =========================================================================
  const setPage = useCallback((p) => {
    setPageStack(prev => [...prev, page]);
    setPageState(p);
  }, [page]);

  const goBack = useCallback(() => {
    setPageStack(prev => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const last  = stack.pop();
      setPageState(last);
      return stack;
    });
  }, []);

  const canGoBack = pageStack.length > 0;

  // =========================================================================
  // LOCATION
  // =========================================================================
  const requestUserLocation = useCallback(() => {
    requestLocation(
      (coords) => { setLocation(coords); saveLocation(coords); },
      (err) => console.warn('[Geo]', err)
    );
  }, []);

  // =========================================================================
  // COMPUTED — users for Discover (from Supabase candidates)
  // =========================================================================
  // Passed profiles are NOT permanently excluded — they cycle back to the end
  // of the queue so users can reconsider. Only liked / blocked are permanent.
  const usersInNetwork = candidates.filter(u =>
    !likes.has(u.id) && u.id !== currentUser?.id
  );

  // getCompatibility — two-directional radar IoU + combined score
  // score1: "Am I what they need?"  (my self-scores vs their need-scores)
  // score2: "Are they what I need?" (my need-scores vs their self-scores)
  // score:  combined (average of both directions)
  const getCompatibility = useCallback((userId) => {
    const candidate = candidates.find(u => u.id === userId);
    if (!candidate) return null;

    const mySelf    = currentUser?.criteria_scores     || {};
    const myNeed    = currentUser?.criteria_scores_fit || {};
    const theirSelf = candidate.criteria_scores        || {};
    const theirNeed = candidate.criteria_scores_fit    || {};

    // Direction 1: am I what they need?
    const score1 = computeRadarCompat(mySelf, theirNeed);
    // Direction 2: are they what I need?
    const score2 = computeRadarCompat(myNeed, theirSelf);
    // Fallback: self vs self (when need scores are unavailable)
    const scoreFallback = computeRadarCompat(mySelf, theirSelf);

    const bothValid = score1 != null && score2 != null;
    const score = bothValid
      ? Math.round((score1 + score2) / 2)
      : (score1 ?? score2 ?? scoreFallback);

    if (score == null) return null;
    return {
      score,
      score1: score1 ?? null,
      score2: score2 ?? null,
      sharedInterests: candidate.shared_interests_count || 0,
    };
  }, [candidates, currentUser]);

  // =========================================================================
  // CONTEXT VALUE
  // =========================================================================
  const value = {
    // Auth
    authUser, currentUser, setCurrentUser,
    loading,
    // Navigation
    page, setPage, goBack, canGoBack, activeChat, setActiveChat,
    // Discover
    users: usersInNetwork, candidates, usersInNetwork,
    likes, passes, superLikes,
    likeUser, passUser, superLikeUser,
    matches,
    acceptConnection, declineConnection,
    getCompatibility,
    // Messages
    conversations, activeMessages, sendMessage,
    // Events
    events, loadEvents,
    registeredEvents, setRegisteredEvents, registerForEvent,
    cancelEventRegistration, joinEventWaitlist, leaveEventWaitlist,
    waitlistEvents, attendedEvents,
    // Scouts
    scouts, setScouts, waitlistEntries,
    joinScoutWaitlist, confirmScout, voteScout, createPrivateScout,
    // Quiz
    quizDone, setQuizDone: setQuizDone,
    showQuizPrompt, setShowQuizPrompt,
    quizPromptMatchId,
    partialQuizAnswers, quizResumeIdx,
    savePartialQuiz, saveQuizAnswers,
    // Profile
    updatePhotos, updateSubscription,
    discoveryPrefs, setDiscoveryPrefs,
    // Notifications
    notifications, unread, markRead, markAllRead,
    // UI prefs
    theme, setTheme, toggleTheme,
    lang, setLang,
    appMode, setAppMode: setAppModeState, switchAppMode,
    features, setFeatures,
    appConfig, setAppConfig,
    profileTabs, setProfileTabs,
    // Location
    location, requestUserLocation,
    // Networks
    networks, activeNetwork, setActiveNetwork,
    networkFeatures, setNetworkFeatures,
    // Helper: is a feature enabled in the active network?
    isNetworkFeatureEnabled: (feature) => networkFeatures[feature] === true,
    // Spotify
    setSpotifyData: (data) => setCurrentUser(u => ({ ...u, spotify: data })),
    // Auth actions
    logout, deleteUserAccount,
    // Boost
    boost, triggerBoost, boostEndsAt,
    // Rewind
    rewindLastPass, lastPassedId,
    // Incognito / Pause / Available / Hive boost
    toggleIncognito,
    togglePause,
    toggleAvailable,
    activateHiveBoost,
    cancelHiveBoost,
    // Subscriptions gate
    subsEnabled: features?.subscriptions_enabled === true,
    // Resolved features — founders_only features resolved per current user
    resolvedFeatures: (() => {
      if (!features) return features;
      const isFounder = currentUser?.is_founding_member === true;
      const resolved = {};
      Object.entries(features).forEach(([k, v]) => {
        resolved[k] = v === '__founders_only__' ? isFounder : v;
      });
      return resolved;
    })(),
    // Compatibility
    NETWORKS: networks,
    userNetworks: networks,
    // Raw supabase client (for admin use only)
    supabase,
  };

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
